import React, { useEffect, useState } from 'react';
import { MyModal } from '../my-modal';
import { Button } from '../button';
import { ConsentCheckboxes } from './ConsentCheckboxes';
import { CONSENT_VERSION } from './legalDocuments';
import { legalApi } from '../../../api/services/legal.service';
import { clearRememberMeData } from '../../../entities/auth/lib/remember-me';

/*
 * Окно согласия при входе. Клиент, у которого нет записи о согласии на
 * обработку ПДн (зарегистрировался до 29.10.2026 или из старой версии
 * приложения), видит его до работы в ЛК. Кому показывать — решает бэк
 * (pd_required: только клиентские роли, сотрудники не спрашиваются).
 * Закрыть окно нельзя: либо согласие, либо выход.
 */
export const ConsentGate: React.FC<{ userId?: number }> = ({ userId }) => {
  const [open, setOpen] = useState(false);
  const [pd, setPd] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!userId) return;
    let cancelled = false;
    legalApi
      .getConsents()
      .then((res) => {
        if (cancelled) return;
        setMarketing(res.data.marketing.granted);
        setOpen(res.data.pd_required);
      })
      .catch(() => {
        /* бэк без приложения legal или сеть — не блокируем работу */
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const accept = async () => {
    if (!pd) return;
    setBusy(true);
    setError('');
    try {
      await legalApi.setConsent({
        kind: 'personal_data',
        granted: true,
        version: CONSENT_VERSION,
        source: 'lk_login_prompt',
      });
      if (marketing) {
        await legalApi.setConsent({
          kind: 'marketing',
          granted: true,
          version: CONSENT_VERSION,
          source: 'lk_login_prompt',
        });
      }
      setOpen(false);
    } catch {
      setError('Не удалось сохранить согласие. Попробуйте ещё раз.');
    } finally {
      setBusy(false);
    }
  };

  const logout = () => {
    clearRememberMeData();
    window.location.href = '/login';
  };

  return (
    <MyModal isOpen={open} onClose={() => undefined} size="md">
      <div className="flex flex-col gap-5">
        <div>
          <h2 className="text-lg font-semibold text-[#2d2c2c]">
            Согласие на обработку персональных данных
          </h2>
          <p className="text-sm text-gray-500 mt-1 leading-relaxed">
            Мы обновили документы о персональных данных. Чтобы продолжить работу в
            личном кабинете, подтвердите согласие на их обработку.
          </p>
        </div>
        <ConsentCheckboxes
          personalData={pd}
          onPersonalDataChange={setPd}
          marketing={marketing}
          onMarketingChange={setMarketing}
        />
        {error && <p className="text-sm text-[#e3282a]">{error}</p>}
        <div className="flex justify-end gap-2">
          <div className="w-[140px]">
            <Button isWhite onClick={logout}>
              Выйти
            </Button>
          </div>
          <div className="w-[180px]">
            <Button onClick={() => { void accept(); }} disabled={!pd} status={busy ? 'loading' : undefined}>
              Продолжить
            </Button>
          </div>
        </div>
      </div>
    </MyModal>
  );
};
